// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@adl/App/Store/Reducers";
import {
    CheckoutStep,
    CHECKOUT_STEPS,
    nextStepHandler,
    previousStepHandler,
} from "@adl/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext } from "@adl/App/Widgets/CheckoutStepper/StepperContext";
import { color, doScroll, getDataLayer, xScreen } from "@adl/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    canPlaceOrder,
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import PlaceOrderButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPlaceOrderButton";
import SubmitForApprovalButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitSubmitForApprovalButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {}

const mapDispatchToProps = {
    updateCart: makeHandlerChainAwaitable(updateCart),
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutStepperActionsStyles {
    container?: InjectableCss;
    prev?: ButtonPresentationProps;
    next?: ButtonPresentationProps;
}

export const styles: CheckoutStepperActionsStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            margin-top: 64px;
            padding-top: 24px;
            border-top: 1px solid ${color("common.border")};
            ${xScreen("lte", "md")} {
                margin-top: 36px;
            }
            ${xScreen("lte", "350")} {
                flex-direction: column;
            }
        `,
    },
    prev: {
        variant: "secondary",
        sizeVariant: "large",
        mergeCss: true,
        css: css`
            ${xScreen("lte", "350")} {
                width: 100%;
                margin-bottom: 24px;
            }
        `,
    },
    next: {
        variant: "primary",
        sizeVariant: "large",
        css: css`
            ${xScreen("lte", "350")} {
                width: 100%;
            }
        `,
    },
};

const CheckoutStepperActions: React.FC<Props> = props => {
    const { currentStep, steps, setCurrentStep, checkoutInfoState } = useContext(StepperContext);
    const history = useHistory();
    const {
        cartPage,
        requestedPickupDate,
        requestedDeliveryDate,
        checkoutShippingPage,
        isLoading,
        cart,
        showPlaceOrderButton,
        showSubmitForApprovalOrder,
        fulfillmentMethod,
        xGAState,
        cartId,
    } = useAppSelector(state => {
        const { cartId } = state.pages.checkoutReviewAndSubmit;
        const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
        return {
            checkoutShippingPage: getPageLinkByPageType(state, "CheckoutShippingPage"),
            cartPage: getPageLinkByPageType(state, "CartPage"),
            isLoading: cartState.isLoading,
            cart: cartState.value,
            showPlaceOrderButton: canPlaceOrder(cartState.value),
            showSubmitForApprovalOrder: canSubmitForApprovalOrder(getCurrentCartState(state).value),
            requestedPickupDate: state.pages.checkoutReviewAndSubmit.requestedPickupDate,
            requestedDeliveryDate: state.pages.checkoutReviewAndSubmit.requestedDeliveryDate,
            fulfillmentMethod: cartState.value?.fulfillmentMethod,
            xGAState: (state as CustomAppState).xGA,
            cartId,
        };
    });

    const goToNextStep = async (nextStep: CheckoutStep) => {
        if (currentStep === CHECKOUT_STEPS.SHIPPING) {
            await props.updateCart({ fullExpand: true });
            if (xGAState.checkoutFlowEvent) {
                const dataLayer = getDataLayer();

                if (!dataLayer) {
                    return;
                }

                const googleAnalyticsData = {
                    event: "add_shipping_info",
                };

                dataLayer.push(googleAnalyticsData);
            }
        }

        if (currentStep !== CHECKOUT_STEPS.ADDRESS) {
            setCurrentStep(nextStep.index);
        }

        if (currentStep === CHECKOUT_STEPS.PAYMENT) {
            if (xGAState.checkoutFlowEvent) {
                const dataLayer = getDataLayer();

                if (!dataLayer) {
                    return;
                }

                const googleAnalyticsData = {
                    event: "add_payment_info",
                };

                dataLayer.push(googleAnalyticsData);
            }
        }

        doScroll(true);
    };

    const url = cart?.type === "Quote" ? `${checkoutShippingPage?.url}?cartId=${cartId}` : checkoutShippingPage?.url;
    const goToPrevStep = (prevStep: CheckoutStep) => {
        if (currentStep === CHECKOUT_STEPS.ADDRESS) {
            history.push(cartPage?.url as string);
        } else if (prevStep.index === CHECKOUT_STEPS.ADDRESS) {
            if (url) {
                history.push(url);
            }
        } else {
            setCurrentStep(prevStep.index);
        }

        doScroll(true);
    };

    const prevStep = previousStepHandler(steps, currentStep);
    const nextStep = nextStepHandler(steps, currentStep);

    const isNextButtonDisabled = () => {
        if (currentStep < 0) {
            return true;
        }
        if (isLoading) {
            return true;
        }
        // XNG-Changes TOR-441: disable next button when paymentMethod doesn't select and currentStep = PAYMENT
        if (currentStep === CHECKOUT_STEPS.PAYMENT && !checkoutInfoState.paymentMethod) {
            return true;
        }
        return false;
    };

    return (
        <StyledWrapper {...styles.container}>
            <Button
                {...styles.prev}
                onClick={() => goToPrevStep(prevStep)}
                disabled={isLoading}
                type={currentStep === CHECKOUT_STEPS.ADDRESS ? "submit" : "button"}
                data-test-selector={`checkout_${prevStep.name || prevStep.displayName}`}
            >
                {translate("Back")}
            </Button>
            {currentStep !== CHECKOUT_STEPS.REVIEW && (
                <Button
                    {...styles.next}
                    onClick={() => goToNextStep(nextStep)}
                    disabled={isNextButtonDisabled()}
                    data-test-selector={`checkout_${nextStep.name || nextStep.displayName}`}
                    type={currentStep === CHECKOUT_STEPS.ADDRESS ? "submit" : "button"}
                >
                    {nextStep.displayName || nextStep.name}
                </Button>
            )}
            {currentStep === CHECKOUT_STEPS.REVIEW && cart && showPlaceOrderButton && (
                <PlaceOrderButton styles={styles.next} />
            )}
            {currentStep === CHECKOUT_STEPS.REVIEW && cart && showSubmitForApprovalOrder && (
                <SubmitForApprovalButton extendedStyles={styles.next} />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CheckoutStepperActions),
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Checkout Stepper Actions",
    },
};

export default widgetModule;
